import { ISelectOption, TTranslator } from '@smartshell/ui-kit';
import dayjs from 'dayjs';
import { ValidateResult } from 'react-hook-form';

import { TLocalesKeys } from 'localization';
import {
  ECountry,
  EOpf,
  IConnectPaymentServiceRequest,
  TTypeBeneficiary,
} from 'settings';
import { Tax } from 'types';

import { ICreateBeneficiaryForm } from './interfaces';

const getCountriesMap = (
  t: TTranslator<TLocalesKeys>
): ISelectOption<ECountry>[] => [
  { value: ECountry.RU, title: t('countries.ru') },
  { value: ECountry.BY, title: t('countries.by'), disabled: true },
  { value: ECountry.KZ, title: t('countries.kz'), disabled: true },
  { value: ECountry.UZ, title: t('countries.uz'), disabled: true },
  { value: ECountry.GE, title: t('countries.ge'), disabled: true },
  { value: ECountry.AM, title: t('countries.am'), disabled: true },
  { value: ECountry.KG, title: t('countries.kg'), disabled: true },
  { value: ECountry.CY, title: t('countries.cy'), disabled: true },
  { value: ECountry.ID, title: t('countries.id'), disabled: true },
  { value: ECountry.BG, title: t('countries.bg'), disabled: true },
];

const dateChanged = (date: string): string =>
  dayjs(date.split('.').reverse().join('.')).format('YYYY-MM-DD');

const getRequestData = (
  field: ICreateBeneficiaryForm,
  type: TTypeBeneficiary
): IConnectPaymentServiceRequest['input'] => {
  const {
    ul,
    bank_name,
    bank_bik,
    bank_kpp,
    bank_account,
    correspondent_account,
    ip,
    passport,
    phone,
    email,
    service_name,
    tax,
  } = field;
  const requestData: IConnectPaymentServiceRequest['input'] = {
    bank_account: {
      bank_name: bank_name.trim(),
      bank_bik,
      bank_account,
      correspondent_account,
      tax: tax || Tax.NONE,
    },
    type: type,
  };

  if (service_name && requestData.bank_account) {
    requestData.bank_account.service_name = service_name.trim();
  }

  if (bank_kpp?.length > 0 && requestData.bank_account) {
    requestData.bank_account.bank_kpp = bank_kpp;
  }

  if (type === 'UL_RESIDENT') {
    const {
      ogrn,
      opf,
      registration_date,
      name,
      legal_entity_address,
      ...ulOther
    } = ul;

    requestData.ul = {
      ...ulOther,
      registration_date: dateChanged(registration_date),
      opf: opf as EOpf,
      phone: `+${phone}`,
      name: name.trim(),
      legal_entity_address: legal_entity_address.trim(),
    };

    if (ogrn) {
      requestData.ul.ogrn = ogrn;
    }

    if (email) {
      requestData.ul.email = email;
    }
  }

  if (type === 'IP_RESIDENT') {
    const {
      middle_name,
      citizenship,
      birth_date,
      registration_date,
      first_name,
      last_name,
      registration_address,
      ...ipOther
    } = ip;

    requestData.ip = {
      ...ipOther,
      birth_date: dateChanged(birth_date),
      registration_date: dateChanged(registration_date),
      citizenship: citizenship as ECountry,
      passport: {
        ...passport,
        date: dateChanged(passport.date),
      },
      phone: `+${phone}`,
      first_name: first_name.trim(),
      last_name: last_name.trim(),
      registration_address: registration_address.trim(),
    };

    if (email) {
      requestData.ip.email = email;
    }

    if (middle_name) {
      requestData.ip.middle_name = middle_name.trim();
    }
  }

  return requestData;
};

const getIsCorrectDate =
  (t: TTranslator<TLocalesKeys>) =>
  (value: string): ValidateResult => {
    if (value.includes('_')) return t('errors.date.incorrect');

    const [day, month, year] = value.split('.');

    if (
      !dayjs(`${year}-${month}-${day}`, 'YYYY-MM-DD', true).isValid() ||
      !dayjs().isAfter(`${year}-${month}-${day}`)
    )
      return t('errors.date.incorrect');
    return true;
  };

const getIsCorrectCode =
  (t: TTranslator<TLocalesKeys>) =>
  (value: string): ValidateResult =>
    !value.includes('_') || t('errors.value.valid');

export { getRequestData, getCountriesMap, getIsCorrectDate, getIsCorrectCode };
